/**
 * The three-step reduction the category leads with and this page used to lack:
 * talk, read, watch it find its place.
 *
 * Each step ends in a small "mini" — a chip-sized replica of the real app
 * state it describes: the recorder's ochre "● Recording" status with its mono
 * timer (the app draws no waveform, so neither does the page), a serif
 * fragment of the transcript appearing, and a linked-note row. The minis are
 * decorative; the step copy carries every claim on its own.
 *
 * Step headings are `<h3>` — one level under the section's `<h2>` with
 * nothing skipped.
 */

import type { ComponentType, ReactNode } from "react";
import { StyleSheet, Text as RawText, View } from "react-native";
import { Card, Text } from "react-native-paper";
import {
  LinkGlyph,
  MicrophoneGlyph,
  TranscriptGlyph,
  type GlyphProps,
} from "@/components/icons";
import { MarketingSection } from "@/components/marketing/section";
import { SectionHead } from "@/components/marketing/section-head";
import { headingProps } from "@/lib/heading-props";
import {
  MONO_STACK,
  SERIF_STACK,
  type AppTheme,
  useAppTheme,
} from "@/theme/paper-theme";
import { iconSize, radius, spacing } from "@/theme/tokens";
import { CHIP_RADIUS } from "@/theme/web-tokens";

const STEP_HEADING_PROPS = headingProps(3);

/**
 * The narrowest a step may be before the row wraps — a flex basis, not a
 * breakpoint; the feature grid uses the same mechanism at its own minimum.
 */
const STEP_MIN_WIDTH = 280;

interface Step {
  ordinal: string;
  heading: string;
  body: string;
  Glyph: ComponentType<GlyphProps>;
  /** The app-state replica the step closes on. */
  mini: (theme: AppTheme) => ReactNode;
}

function RecordingMini({ theme }: { theme: AppTheme }) {
  return (
    <View
      aria-hidden
      style={[styles.mini, { borderColor: theme.colors.outlineVariant }]}
    >
      <View style={[styles.dot, { backgroundColor: theme.colors.tertiary }]} />
      <RawText style={[styles.miniLive, { color: theme.colors.tertiary }]}>
        Recording
      </RawText>
      <RawText style={[styles.miniMono, { color: theme.colors.onSurface }]}>
        00:05
      </RawText>
    </View>
  );
}

function TranscriptMini({ theme }: { theme: AppTheme }) {
  return (
    <View
      aria-hidden
      style={[styles.mini, { borderColor: theme.colors.outlineVariant }]}
    >
      <RawText style={[styles.miniSerif, { color: theme.colors.onSurface }]}>
        “…beats cramming, every time.”
      </RawText>
    </View>
  );
}

function LinkedMini({ theme }: { theme: AppTheme }) {
  return (
    <View
      aria-hidden
      style={[styles.mini, { borderColor: theme.colors.outlineVariant }]}
    >
      <LinkGlyph size={iconSize.sm} color={theme.colors.primary} />
      <RawText style={[styles.miniText, { color: theme.colors.onSurface }]}>
        Make It Stick — ch. 3
      </RawText>
      <RawText style={[styles.miniMono, { color: theme.colors.onSurface }]}>
        00:12
      </RawText>
    </View>
  );
}

const STEPS: readonly Step[] = [
  {
    ordinal: "STEP 1",
    heading: "Talk",
    body: "Hit record and think out loud. There's no time limit, and a phone call or a dead battery can't lose what you've said.",
    Glyph: MicrophoneGlyph,
    mini: (theme) => <RecordingMini theme={theme} />,
  },
  {
    ordinal: "STEP 2",
    heading: "Read",
    body: "The words show up while you're still talking. Your iPhone does the writing. Stop talking, start reading.",
    Glyph: TranscriptGlyph,
    mini: (theme) => <TranscriptMini theme={theme} />,
  },
  {
    ordinal: "STEP 3",
    heading: "Watch it find its place",
    body: "Sparkie notices what a new note is about and links it to the ones you've already made about the same thing.",
    Glyph: LinkGlyph,
    mini: (theme) => <LinkedMini theme={theme} />,
  },
];

export function HowItWorks() {
  const theme = useAppTheme();

  return (
    <MarketingSection style={styles.section}>
      <SectionHead
        eyebrow="How it works"
        heading="Three steps, and two of them are automatic."
      />

      <View style={styles.grid}>
        {STEPS.map(({ ordinal, heading, body, Glyph, mini }) => (
          <Card key={heading} mode="outlined" style={styles.step}>
            <Card.Content style={styles.stepContent}>
              <RawText
                style={[
                  styles.ordinal,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                {ordinal}
              </RawText>

              <View
                style={[
                  styles.glyphBox,
                  { backgroundColor: theme.colors.primaryContainer },
                ]}
              >
                <Glyph size={iconSize.md} color={theme.colors.primary} />
              </View>

              <Text
                variant="titleLarge"
                style={{ color: theme.colors.onSurface }}
                {...STEP_HEADING_PROPS}
              >
                {heading}
              </Text>

              <Text
                variant="bodyMedium"
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                {body}
              </Text>

              {mini(theme)}
            </Card.Content>
          </Card>
        ))}
      </View>
    </MarketingSection>
  );
}

const styles = StyleSheet.create({
  section: {
    gap: spacing.xl,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.lg,
  },
  step: {
    flexBasis: STEP_MIN_WIDTH,
    flexGrow: 1,
    // Same guard as the feature cards: RN's flexShrink defaults to 0, and a
    // viewport narrower than basis + gutters must shrink, not overflow.
    flexShrink: 1,
  },
  stepContent: {
    gap: spacing.sm,
    alignItems: "flex-start",
  },
  ordinal: {
    fontFamily: MONO_STACK,
    fontSize: 12,
    letterSpacing: 1.2,
  },
  glyphBox: {
    width: 44,
    height: 44,
    borderRadius: CHIP_RADIUS,
    alignItems: "center",
    justifyContent: "center",
  },
  mini: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    borderWidth: 1,
    borderRadius: CHIP_RADIUS,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm + 2,
    marginTop: spacing.xxs,
  },
  dot: {
    width: 9,
    height: 9,
    borderRadius: radius.full,
  },
  miniLive: {
    fontSize: 13.5,
    fontWeight: "600",
  },
  miniMono: {
    fontFamily: MONO_STACK,
    fontSize: 13,
  },
  miniSerif: {
    fontFamily: SERIF_STACK,
    fontSize: 14,
  },
  miniText: {
    fontSize: 13.5,
  },
});
